import {
  DeliveryEstimateT,
  DeliveryType,
  OfferT,
} from '@lambda/apis/src/catalog-v2/types';
import {
  CUSTOMER_STATE_ZIPCODE,
  getDefaultZipcodeState,
  parseZipcodeState,
} from './customer-zipcode';

/** The place a delivery estimate is quoted for. */
export type DeliveryDestinationT = {
  state: string | null;
  region: string | null;
};

export const resolveDeliveryDestination = (
  cookies: Partial<Record<string, string>> = {},
): DeliveryDestinationT => {
  const stored =
    parseZipcodeState(cookies[CUSTOMER_STATE_ZIPCODE]) ??
    getDefaultZipcodeState();

  return { state: stored?.state ?? null, region: stored?.region ?? null };
};

export const findDeliveryEstimate = (
  estimates: DeliveryEstimateT[] | undefined,
  type: DeliveryType,
  state: string | null = null,
  region: string | null = null,
): DeliveryEstimateT | undefined => {
  const list = estimates ?? [];

  // 1. Exact match: type + state + region
  if (state && region) {
    const exact = list.find(
      (d) =>
        d.type === type &&
        d.destinationState === state &&
        d.destinationRegion === region,
    );

    if (exact) {
      return exact;
    }
  }

  // 2. State match: type + state (non-region stores or region not yet resolved)
  if (state) {
    const stateMatch = list.find(
      (d) => d.type === type && d.destinationState === state,
    );

    if (stateMatch) {
      return stateMatch;
    }
  }

  // 3. Fallback: first of this type
  return list.find((d) => d.type === type);
};

export const trimDeliveryEstimates = (
  estimates: DeliveryEstimateT[] | undefined,
  destination: DeliveryDestinationT,
): DeliveryEstimateT[] => {
  const types = Array.from(new Set((estimates ?? []).map((e) => e.type)));

  return types.flatMap((type) => {
    const picked = findDeliveryEstimate(
      estimates,
      type,
      destination.state,
      destination.region,
    );

    return picked ? [picked] : [];
  });
};

export const trimOfferDeliveryEstimates = <
  T extends Pick<OfferT, 'deliveryEstimates'>,
>(
  offers: T[] | undefined,
  destination: DeliveryDestinationT,
): T[] =>
  (offers ?? []).map((offer) => ({
    ...offer,
    deliveryEstimates: trimDeliveryEstimates(
      offer.deliveryEstimates,
      destination,
    ),
  }));
